import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

export const CompanyManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [companyForm, setCompanyForm] = useState<any>(null);
  const [addressForms, setAddressForms] = useState<any[]>([]);

  const { data: companyInfo, isLoading: isLoadingInfo } = useQuery({
    queryKey: ["company-info"],
    queryFn: async () => {
      console.log("Fetching company info...");
      const { data, error } = await supabase
        .from("sns_company_info")
        .select("*")
        .maybeSingle();
      if (error) throw error;
      
      // Initialize form state
      if (data && !companyForm) {
        setCompanyForm(data);
      }
      
      return data;
    }
  });

  const { data: addresses, isLoading: isLoadingAddresses } = useQuery({
    queryKey: ["company-addresses"],
    queryFn: async () => {
      console.log("Fetching company addresses...");
      const { data, error } = await supabase
        .from("company_addresses")
        .select("*")
        .order('is_primary', { ascending: false });
      if (error) throw error;
      
      // Initialize form state for addresses
      if (data && addressForms.length === 0) {
        setAddressForms(data);
      }
      
      return data;
    }
  });

  const updateCompanyInfo = useMutation({
    mutationFn: async (info: typeof companyInfo) => {
      console.log("Updating company info:", info);
      const { error } = await supabase
        .from("sns_company_info")
        .upsert(info);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-info"] });
      toast({
        title: "Success",
        description: "Company information updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update company information: " + error.message,
        variant: "destructive",
      });
    },
  });

  const updateAddress = useMutation({
    mutationFn: async (address: any) => {
      console.log("Updating address:", address);
      const { error } = await supabase
        .from("company_addresses")
        .upsert(address);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-addresses"] });
      toast({
        title: "Success",
        description: "Address updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update address: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deleteAddress = useMutation({
    mutationFn: async (id: number) => {
      console.log("Deleting address:", id);
      const { error } = await supabase
        .from("company_addresses")
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-addresses"] });
      toast({
        title: "Success",
        description: "Address deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete address: " + error.message,
        variant: "destructive",
      });
    },
  });

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = event.target.files?.[0];
      if (!file) return;

      const fileExt = file.name.split(".").pop();
      const fileName = `logo.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("company-logos")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("company-logos")
        .getPublicUrl(fileName);

      const updatedForm = {
        ...companyForm,
        logo_url: publicUrl
      };
      
      setCompanyForm(updatedForm);
      await updateCompanyInfo.mutateAsync(updatedForm);

      toast({
        title: "Success",
        description: "Logo updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to upload logo: " + error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const addNewAddress = () => {
    const newAddress = {
      name: "New Office",
      address: "",
      city: "",
      country: "",
      phone: "",
      email: "",
      is_primary: addresses?.length === 0
    };
    
    updateAddress.mutate(newAddress);
  };
  
  const handleSaveCompanyInfo = () => {
    if (companyForm) {
      updateCompanyInfo.mutate(companyForm);
    }
  };
  
  const updateAddressField = (address: any, field: string, value: any) => {
    const updatedAddress = { ...address, [field]: value };
    
    // Update local state
    setAddressForms(addressForms.map(a => 
      a.id === address.id ? updatedAddress : a
    ));
    
    // Save to database
    updateAddress.mutate(updatedAddress);
  };

  if (isLoadingInfo || isLoadingAddresses) {
    return <div className="p-6 flex justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Company Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="logo">Logo</Label>
            <div className="flex items-center gap-4">
              {companyForm?.logo_url && (
                <img
                  src={companyForm.logo_url}
                  alt="Company logo"
                  className="w-32 h-32 object-contain"
                />
              )}
              <Input
                id="logo"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Company Name</Label>
            <Input
              id="name"
              value={companyForm?.name || ""}
              onChange={(e) => setCompanyForm({
                ...companyForm,
                name: e.target.value
              })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slogan">Slogan</Label>
            <Input
              id="slogan"
              value={companyForm?.slogan || ""}
              onChange={(e) => setCompanyForm({
                ...companyForm,
                slogan: e.target.value
              })}
            />
          </div>
          
          <Button 
            onClick={handleSaveCompanyInfo}
            disabled={updateCompanyInfo.isPending}
          >
            {updateCompanyInfo.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            Save Company Info
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Company Addresses</CardTitle>
          <Button onClick={addNewAddress} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Address
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {addresses?.map((address: any) => (
              <Card key={address.id}>
                <CardContent className="pt-6">
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`name-${address.id}`}>Office Name</Label>
                      <Input
                        id={`name-${address.id}`}
                        value={address.name || ""}
                        onChange={(e) => updateAddressField(address, 'name', e.target.value)}
                        placeholder="e.g., Main Office, Branch Office"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`address-${address.id}`}>Address</Label>
                      <Input
                        id={`address-${address.id}`}
                        value={address.address || ""}
                        onChange={(e) => updateAddressField(address, 'address', e.target.value)}
                        placeholder="Street address"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`city-${address.id}`}>City</Label>
                        <Input
                          id={`city-${address.id}`}
                          value={address.city || ""}
                          onChange={(e) => updateAddressField(address, 'city', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`country-${address.id}`}>Country</Label>
                        <Input
                          id={`country-${address.id}`}
                          value={address.country || ""}
                          onChange={(e) => updateAddressField(address, 'country', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`phone-${address.id}`}>Phone</Label>
                        <Input
                          id={`phone-${address.id}`}
                          value={address.phone || ""}
                          onChange={(e) => updateAddressField(address, 'phone', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`email-${address.id}`}>Email</Label>
                        <Input
                          id={`email-${address.id}`}
                          value={address.email || ""}
                          onChange={(e) => updateAddressField(address, 'email', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`latitude-${address.id}`}>Latitude</Label>
                        <Input
                          id={`latitude-${address.id}`}
                          type="number"
                          step="any"
                          value={address.latitude || ""}
                          onChange={(e) => updateAddressField(address, 'latitude', parseFloat(e.target.value) || null)}
                          placeholder="e.g., 4.175"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`longitude-${address.id}`}>Longitude</Label>
                        <Input
                          id={`longitude-${address.id}`}
                          type="number"
                          step="any"
                          value={address.longitude || ""}
                          onChange={(e) => updateAddressField(address, 'longitude', parseFloat(e.target.value) || null)}
                          placeholder="e.g., 73.509"
                        />
                      </div>
                    </div>


                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`primary-${address.id}`}
                          checked={address.is_primary}
                          onChange={(e) => updateAddressField(address, 'is_primary', e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor={`primary-${address.id}`}>Primary Address</Label>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteAddress.mutate(address.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanyManager;